<script lang="ts" setup>
import { UserRole } from 'cesg-shared/dist/models';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

interface GuestDetails {
  email: string;
  name: string;
  role: UserRole;
}

const { t } = useI18n();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'invite', guestDetails: GuestDetails): void;
}>();

const guestDetails = ref<Partial<GuestDetails>>({
  role: UserRole.GUEST,
});

const inviteGuest = () => {
  if (
    !guestDetails.value.email ||
    !guestDetails.value.name ||
    !guestDetails.value.role
  ) {
    return;
  }
  emit('invite', guestDetails.value as GuestDetails);
  emit('close');
};
</script>

<template>
  <div class="modal-overlay" @click.self="emit('close')">
    <div class="modal-box">
      <div class="modal-header">
        <h3 class="modal-title">{{ t('adminProjects.inviteUser') }}</h3>
        <button class="close-button" @click="emit('close')">&times;</button>
      </div>

      <div class="modal-content">
        <div class="form-field">
          <label for="guest-email">{{ t('email') }}</label>
          <input
            id="guest-email"
            v-model="guestDetails.email"
            type="email"
            placeholder="guest@example.com"
          />
        </div>

        <div class="form-field">
          <label for="guest-name">{{ t('name') }}</label>
          <input
            id="guest-name"
            v-model="guestDetails.name"
            type="text"
            placeholder="Guest Name"
          />
        </div>

        <div class="form-field">
          <label for="guest-role">{{ t('role') }}</label>
          <select id="guest-role" v-model="guestDetails.role">
            <option :value="UserRole.ADMIN">
              {{ t(`roles.${UserRole.ADMIN}`) }}
            </option>
            <option :value="UserRole.CESG">
              {{ t(`roles.${UserRole.CESG}`) }}
            </option>
            <option :value="UserRole.GUEST">
              {{ t(`roles.${UserRole.GUEST}`) }}
            </option>
          </select>
        </div>
      </div>

      <div class="modal-buttons">
        <button
          class="button invite-button"
          :disabled="!guestDetails.email || !guestDetails.name"
          @click="inviteGuest"
        >
          Invite
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use '@/assets/style/popup';

.modal-content {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  label {
    font-weight: 600;
    font-size: 0.9rem;
  }

  input,
  select {
    padding: 0.5rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 1rem;

    &:focus {
      outline: none;
      border-color: #3273dc;
    }
  }
}

.modal-buttons {
  padding: 1rem 1.5rem;
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid #eee;
}

.invite-button {
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}
</style>
